/**
 * Capture `beforeinstallprompt` as early as possible.
 *
 * BIP often fires before deferred `type=module` JS runs (SW already controlling
 * the page from a prior visit). Pair this module with the tiny inline script in
 * each platform `index.html` so the event is never missed.
 */

export type DeferredInstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

export type PromptInstallResult = 'accepted' | 'dismissed' | 'unavailable' | 'error';

declare global {
  interface Window {
    __platzonaDeferredInstall?: DeferredInstallPrompt | null;
    __platzonaBipCaptureInstalled?: boolean;
  }
}

export const PLATZONA_BIP_EVENT = 'platzona:beforeinstallprompt';

/** Idempotent — safe from index.html inline script + main.tsx. */
export function installEarlyBeforeInstallPromptCapture(): void {
  if (typeof window === 'undefined') return;
  if (window.__platzonaDeferredInstall === undefined) {
    window.__platzonaDeferredInstall = null;
  }
  if (window.__platzonaBipCaptureInstalled) return;
  window.__platzonaBipCaptureInstalled = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    window.__platzonaDeferredInstall = event as DeferredInstallPrompt;
    window.dispatchEvent(new CustomEvent(PLATZONA_BIP_EVENT));
  });
  window.addEventListener('appinstalled', () => {
    window.__platzonaDeferredInstall = null;
    try {
      // Lazy import avoided — mark via localStorage key shared with pwaInstallPresence.
      const host = window.location.host || 'local';
      localStorage.setItem(`platzona-pwa-on-device:${host}`, String(Date.now()));
    } catch {
      /* ignore */
    }
  });
}

export function getDeferredInstallPrompt(): DeferredInstallPrompt | null {
  if (typeof window === 'undefined') return null;
  return window.__platzonaDeferredInstall || null;
}

export function clearDeferredInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.__platzonaDeferredInstall = null;
}

/** Call from a direct user gesture (button click/touch). */
export async function promptDeferredInstall(): Promise<PromptInstallResult> {
  const deferred = getDeferredInstallPrompt();
  if (!deferred?.prompt) return 'unavailable';
  try {
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      clearDeferredInstallPrompt();
      return 'accepted';
    }
    return 'dismissed';
  } catch {
    return 'error';
  }
}
